import { SchoolAutocomplete } from "@/components/SchoolAutocomplete";
import { resolveAttribution, type ResolvedAttribution } from "@/lib/firstTouch";
import { useState, useEffect } from 'react';
import { metaViewContent, metaSignupStart, metaCompleteRegistration } from "@/lib/metaPixel";
import { AlertCircle, BookOpen, Trophy, Users } from "lucide-react";
import { AuthShell, PasswordInput, fieldClass } from "@/components/auth/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { useTerritory } from "@/contexts/TerritoryContext";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";

const TERRITORY_TO_COUNTRY_CODE: Record<string, string> = {
  jm: '1876',
  tt: '1868',
  gy: '592',
  bs: '1242',
  lc: '1758',
};

const COUNTRY_CODES = [
  { code: '1876', label: '\u{1F1EF}\u{1F1F2} +1876', name: 'Jamaica (876)' },
  { code: '1658', label: '\u{1F1EF}\u{1F1F2} +1658', name: 'Jamaica (658)' },
  { code: '1868', label: '\u{1F1F9}\u{1F1F9} +1868', name: 'Trinidad & Tobago' },
  { code: '1246', label: '\u{1F1E7}\u{1F1E7} +1246', name: 'Barbados' },
  { code: '1242', label: '\u{1F1E7}\u{1F1F8} +1242', name: 'Bahamas' },
  { code: '1473', label: '\u{1F1EC}\u{1F1E9} +1473', name: 'Grenada' },
  { code: '1758', label: '\u{1F1F1}\u{1F1E8} +1758', name: 'St. Lucia' },
  { code: '1268', label: '\u{1F1E6}\u{1F1EC} +1268', name: 'Antigua & Barbuda' },
  { code: '1767', label: '\u{1F1E9}\u{1F1F2} +1767', name: 'Dominica' },
  { code: '1784', label: '\u{1F1FB}\u{1F1E8} +1784', name: 'St. Vincent' },
  { code: '1869', label: '\u{1F1F0}\u{1F1F3} +1869', name: 'St. Kitts & Nevis' },
  { code: '592', label: '\u{1F1EC}\u{1F1FE} +592', name: 'Guyana' },
  { code: '501', label: '\u{1F1E7}\u{1F1FF} +501', name: 'Belize' },
  { code: '597', label: '\u{1F1F8}\u{1F1F7} +597', name: 'Suriname' },
  { code: '1345', label: '\u{1F1F0}\u{1F1FE} +1345', name: 'Cayman Islands' },
  { code: '1649', label: '\u{1F1F9}\u{1F1E8} +1649', name: 'Turks & Caicos' },
  { code: '1284', label: '\u{1F1FB}\u{1F1EC} +1284', name: 'BVI' },
  { code: '1340', label: '\u{1F1FB}\u{1F1EE} +1340', name: 'USVI' },
  { code: '1441', label: '\u{1F1E7}\u{1F1F2} +1441', name: 'Bermuda' },
  { code: '1264', label: '\u{1F1E6}\u{1F1EE} +1264', name: 'Anguilla' },
  { code: '1664', label: '\u{1F1F2}\u{1F1F8} +1664', name: 'Montserrat' },
  { code: '5999', label: '\u{1F1E8}\u{1F1FC} +5999', name: 'Curacao' },
  { code: '1', label: '\u{1F1FA}\u{1F1F8} +1', name: 'United States' },
  { code: '44', label: '\u{1F1EC}\u{1F1E7} +44', name: 'United Kingdom' },
  { code: '1_ca', label: '\u{1F1E8}\u{1F1E6} +1', name: 'Canada' },
];

export default function Signup() {
  const { data: grades = [] } = useQuery<Array<{id: string; gradeNumber: number; name: string; displayName?: string; isActive: boolean; sortOrder?: number}>>({
    queryKey: ['/api/curriculum/grades'],
  });

  const apiGrades = grades
    .filter(g => g.isActive)
    .sort((a, b) => (a.sortOrder ?? a.gradeNumber) - (b.sortOrder ?? b.gradeNumber));

  const hasGrade11 = apiGrades.some(g => g.gradeNumber === 11);
  const activeGrades = hasGrade11
    ? apiGrades
    : [...apiGrades, { id: 'grade-11', gradeNumber: 11, name: 'Grade 11', isActive: true }];

  const { territory, territoryCode, isDetecting } = useTerritory();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    countryCode: TERRITORY_TO_COUNTRY_CODE[territoryCode] || '1876',
    phoneNumber: '',
    gradeLevel: 0,
    school: '',
    marketingOptIn: true,
    voucherCode: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { register, isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  // Auto-select country code when territory detection completes
  useEffect(() => {
    if (!isDetecting) {
      const code = TERRITORY_TO_COUNTRY_CODE[territoryCode];
      if (code) {
        setFormData(prev => ({ ...prev, countryCode: code }));
      }
    }
  }, [territoryCode, isDetecting]);

  useEffect(() => {
    if (!isAuthenticated) return;
    // Read the URL directly: this effect can run before the param-capture effect below has
    // populated offerLink, and an offer link must not dump a signed-in parent on the dashboard.
    const sp = new URLSearchParams(window.location.search);
    const code = sp.get("code");
    if (code) {
      const params = new URLSearchParams({ code });
      const token = sp.get("o");
      if (token) params.set("o", token);
      setLocation(`/subscribe?${params.toString()}`);
      return;
    }
    setLocation('/dashboard');
  }, [isAuthenticated, setLocation]);

  // Capture UTM / ref params from the URL (e.g. ManyChat-sourced signups land
  // with ?ref=manychat&grade=5&email=...&utm_source=instagram). Pre-fill the
  // form fields and stash the attribution params so they're submitted with
  // the registration.
  const [attribution, setAttribution] = useState<ResolvedAttribution>({});
  // An offer code (and its signed deadline token) arrives on the signup link from the PEP
  // readiness check and from campaign emails. Subscribe auto-applies ?code=, but a parent has to
  // create the account first, so the code has to survive this hop or the offer is silently lost.
  const [offerLink, setOfferLink] = useState<{ code?: string; token?: string }>({});

  function subscribeHref() {
    const params = new URLSearchParams();
    if (offerLink.code) params.set("code", offerLink.code);
    if (offerLink.token) params.set("o", offerLink.token);
    const qs = params.toString();
    return qs ? `/subscribe?${qs}` : "/subscribe";
  }

  // The campaign's landing page rendered. Diagnostic only; optimisation stays on
  // CompleteRegistration, per the campaign brief.
  useEffect(() => { metaViewContent("Signup"); }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    const ref = sp.get("ref") || undefined;
    const utmSource = sp.get("utm_source") || undefined;
    const utmMedium = sp.get("utm_medium") || undefined;
    const utmCampaign = sp.get("utm_campaign") || undefined;
    const email = sp.get("email") || "";
    const name = sp.get("name") || "";
    const gradeRaw = sp.get("grade") || "";
    const phone = sp.get("phone") || "";
    const offerCode = sp.get("code") || undefined;
    const offerToken = sp.get("o") || undefined;
    if (offerCode) setOfferLink({ code: offerCode, token: offerToken });
    if (offerCode) setFormData((prev) => ({ ...prev, voucherCode: prev.voucherCode || offerCode }));

    // Scrub personal data out of the address bar now it has been read into state.
    //
    // Our own campaign emails link to /signup?email=parent@example.com, and the Meta Pixel sends
    // the current page URL with every event, so the raw address would reach Meta in plaintext
    // beside the hash meant to protect it. It also sits in the Referer of the registration
    // request, in browser history, and in any screenshot the parent takes. Identity params go;
    // attribution params stay, because the funnel report depends on them.
    if (sp.has("email") || sp.has("name") || sp.has("phone")) {
      const keep = new URLSearchParams();
      for (const [k, v] of sp.entries()) {
        if (!["email", "name", "phone"].includes(k)) keep.set(k, v);
      }
      const qs = keep.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
    }

    // One shared resolver for every page that can create an account, so /signup and the paid
    // landing pages can never disagree about where a registration came from. It layers current
    // URL params over the stored first touch over the referrer, and now carries utm_content,
    // utm_term and fbclid, which the campaign brief requires and this page used to drop.
    setAttribution(resolveAttribution());

    setFormData((prev) => ({
      ...prev,
      firstName: prev.firstName || name,
      email: prev.email || email,
      gradeLevel: prev.gradeLevel || (gradeRaw ? parseInt(gradeRaw, 10) || 0 : 0),
      phoneNumber: prev.phoneNumber || phone.replace(/\D/g, "").replace(/^1/, "").slice(0, 10),
    }));
  }, []);

  const handleChange = (field: string, value: string | number | boolean) => {
    // First real interaction with the form. Fires once, and gives us the signup-start rate the
    // measurement plan asks for, which is what separates "the page is wrong" from "the audience
    // is wrong" when cost per registration looks bad.
    metaSignupStart();
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getFullPhone = () => {
    let dialCode = formData.countryCode === '1_ca' ? '1' : formData.countryCode;
    // All NANP Caribbean/North American codes (1876, 1868, 1246, etc.) use +1 as the
    // actual country code: the 3-digit area code is part of the 10-digit subscriber number.
    // Using the full 4-digit code (e.g. 1876) would double-up the area code.
    if (dialCode.length === 4 && dialCode.startsWith('1')) {
      dialCode = '1';
    }
    return dialCode + formData.phoneNumber;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (formData.password.length < 8) {
      setErrorMessage("Your password must be at least 8 characters.");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setErrorMessage("Passwords don't match. Please make sure both are the same.");
      return;
    }

    if (!formData.gradeLevel) {
      setErrorMessage("Please select a grade level to continue.");
      return;
    }

    if (!formData.phoneNumber || formData.phoneNumber.length < 7) {
      setErrorMessage("Please enter a valid phone number.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      try {
        await fetch('/api/track-signup-attempt', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            grade: formData.gradeLevel,
            school: formData.school || undefined,
            phone: getFullPhone()
          })
        });
      } catch (trackError) {
        console.error('Failed to track signup attempt:', trackError);
      }

      const result = await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        grade: formData.gradeLevel,
        school: formData.school || undefined,
        parentPhone: getFullPhone(),
        marketingOptIn: formData.marketingOptIn,
        voucherCode: formData.voucherCode.trim() || undefined,
        ...attribution,
      });
      // Only a genuinely new account counts. The server sends registrationEventId ONLY on the
      // path where it created one, and omits it entirely for a resumed signup, so this cannot
      // fire for a returning user. The id is shared with the server's Conversions API event so
      // Meta deduplicates the pair rather than counting the signup twice.
      metaCompleteRegistration((result as any)?.registrationEventId);

      const redeemed = (result as any)?.voucherRedeemed as { code: string; durationDays: number } | null | undefined;
      if (redeemed) {
        toast({
          title: "Voucher applied",
          description: `Your account is unlocked for ${redeemed.durationDays} days. No payment needed.`,
        });
        setLocation('/dashboard');
        return;
      }
      if ((result as any)?.resumed) {
        toast({
          title: "Welcome back!",
          description: "We found your existing account. Let's finish setting up your subscription.",
        });
      } else {
        toast({
          title: "Welcome to EduFocal!",
          description: "Your account has been created successfully. Now let's set up your subscription!",
        });
      }
      // A promo code typed at signup (not a voucher) travels to Subscribe, where it is validated.
      const typedCode = formData.voucherCode.trim().toUpperCase();
      if (typedCode && !offerLink.code) {
        setLocation(`/subscribe?code=${encodeURIComponent(typedCode)}`);
        return;
      }
      setLocation(subscribeHref());
    } catch (error: any) {
      const msg = error?.message || '';
      let friendlyMessage = "Something went wrong. Please try again.";
      if (msg.includes("Please log in to continue")) {
        friendlyMessage = "This email is already registered. Check your password or head to the login page.";
      } else if (msg.includes("Please log in")) {
        friendlyMessage = "This email already has an active account. Please log in instead.";
      } else if (msg.includes("password") || msg.includes("Password")) {
        friendlyMessage = msg;
      }
      setErrorMessage(friendlyMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthShell
      illustration="owl-rocket"
      headline="Join thousands of"
      accent="successful students"
      subtitle={`Practice for ${territory.examName} with Owlbert: quizzes that explain themselves, lessons, readers and real prizes.`}
      features={[
        { icon: <BookOpen className="w-5 h-5" />, title: `Complete ${territory.examName} curriculum`, text: "All subjects with practice questions, explanations and progress tracking.", testId: "feature-curriculum" },
        { icon: <Trophy className="w-5 h-5" />, title: "Learning that feels like a game", text: "Earn XP, unlock badges, climb the leaderboard, win gift cards.", testId: "feature-gamified" },
        { icon: <Users className="w-5 h-5" />, title: "Learn with classmates", text: "Follow friends, send challenges and share the wins.", testId: "feature-community" },
      ]}
      stats={[
        { value: "106,067", label: "Learners taught", testId: "stat-students" },
        { value: "25,357", label: "Questions in the bank", testId: "stat-questions" },
        { value: "2011", label: "Teaching since", testId: "stat-since" },
      ]}
      formTitle="Create your account"
      formSubtitle="Takes a minute. Choose a plan right after."
      footer={
        <p>Already have an account? <button type="button" className="text-primary font-semibold hover:underline" onClick={() => setLocation('/login')} data-testid="link-sign-in">Sign in</button></p>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <fieldset className="space-y-4">
          <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">The student</legend>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="firstName" className="text-sm font-semibold">First name</Label>
              <Input id="firstName" placeholder="First name" value={formData.firstName} onChange={(e) => handleChange('firstName', e.target.value)} required autoComplete="given-name" className={fieldClass} data-testid="input-first-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lastName" className="text-sm font-semibold">Last name</Label>
              <Input id="lastName" placeholder="Last name" value={formData.lastName} onChange={(e) => handleChange('lastName', e.target.value)} required autoComplete="family-name" className={fieldClass} data-testid="input-last-name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="gradeLevel" className="text-sm font-semibold">Grade</Label>
              <Select value={formData.gradeLevel ? formData.gradeLevel.toString() : undefined} onValueChange={(value) => handleChange('gradeLevel', parseInt(value))}>
                <SelectTrigger id="gradeLevel" className={fieldClass} data-testid="select-grade-level"><SelectValue placeholder="Pick a grade" /></SelectTrigger>
                <SelectContent>
                  {activeGrades.map((g) => (<SelectItem key={g.id} value={g.gradeNumber.toString()}>{g.displayName || g.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="school" className="text-sm font-semibold">School <span className="font-normal text-muted-foreground">(optional)</span></Label>
              <SchoolAutocomplete id="school" placeholder="School name" value={formData.school} onChange={(v) => handleChange('school', v)} className={fieldClass} data-testid="input-school" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="voucherCode" className="text-sm font-semibold">Voucher or promo code <span className="font-normal text-muted-foreground">(optional)</span></Label>
            <Input id="voucherCode" placeholder="e.g. ABCD-EFGH-JKLM" value={formData.voucherCode} onChange={(e) => handleChange('voucherCode', e.target.value.toUpperCase())} autoCapitalize="characters" autoCorrect="off" spellCheck={false} className={`${fieldClass} font-mono tracking-wide`} data-testid="input-voucher-code" />
            <p className="text-xs text-muted-foreground">Got a voucher from a school, partner or EduFocal? It unlocks the account straight away. Promo codes are applied on the next screen.</p>
          </div>
        </fieldset>

        <fieldset className="space-y-4">
          <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">The account</legend>
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-sm font-semibold">Email</Label>
            <Input id="email" type="email" placeholder="you@example.com" value={formData.email} onChange={(e) => handleChange('email', e.target.value)} required autoComplete="email" className={fieldClass} data-testid="input-email" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm font-semibold">Password</Label>
              <PasswordInput id="password" placeholder="At least 8 characters" value={formData.password} onChange={(e) => handleChange('password', e.target.value)} required autoComplete="new-password" data-testid="input-password" />
              {formData.password.length > 0 && formData.password.length < 8 && (
                <p className="text-xs text-destructive">{8 - formData.password.length} more character{8 - formData.password.length !== 1 ? 's' : ''} needed</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirmPassword" className="text-sm font-semibold">Confirm password</Label>
              <PasswordInput id="confirmPassword" placeholder="Type it again" value={formData.confirmPassword} onChange={(e) => handleChange('confirmPassword', e.target.value)} required autoComplete="new-password" data-testid="input-confirm-password" />
              {formData.confirmPassword.length > 0 && (
                formData.password === formData.confirmPassword
                  ? <p className="text-xs text-emerald-700 dark:text-emerald-300">Passwords match</p>
                  : <p className="text-xs text-destructive">Passwords don't match</p>
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="phoneNumber" className="text-sm font-semibold">Parent or guardian phone</Label>
            <div className="flex gap-2">
              <div className="w-[42%]">
                <Select value={formData.countryCode} onValueChange={(value) => handleChange('countryCode', value)}>
                  <SelectTrigger className={fieldClass} data-testid="select-country-code"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COUNTRY_CODES.map((country) => (<SelectItem key={country.code} value={country.code}>{country.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Input
                  id="phoneNumber" type="tel" inputMode="numeric" placeholder="7-digit number" value={formData.phoneNumber}
                  onChange={(e) => {
                    let digits = e.target.value.replace(/\D/g, '');
                    let effectiveCode = formData.countryCode === '1_ca' ? '1' : formData.countryCode;
                    if (effectiveCode.length === 4 && effectiveCode.startsWith('1')) effectiveCode = '1';
                    if (digits.startsWith(effectiveCode)) digits = digits.slice(effectiveCode.length);
                    handleChange('phoneNumber', digits);
                  }}
                  required autoComplete="tel-national" className={fieldClass} data-testid="input-phone-number"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Local number only. The area code is in the dropdown. Gift cards and updates go to this phone.</p>
          </div>
        </fieldset>

        <label htmlFor="marketingOptIn" className="flex items-start gap-3 rounded-xl border border-border bg-primary/5 px-3 py-3 cursor-pointer">
          <Checkbox id="marketingOptIn" checked={formData.marketingOptIn} onCheckedChange={(checked) => handleChange('marketingOptIn', checked === true)} className="mt-0.5" data-testid="checkbox-marketing-opt-in" />
          <span className="text-sm leading-snug">Send me tips, offers and news from EduFocal Academy. You can change this any time.</span>
        </label>

        {errorMessage && (
          <div className="flex items-start gap-2.5 rounded-xl bg-rose-500/10 border border-rose-500/40 px-4 py-3" role="alert">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-rose-600" />
            <p className="text-sm text-rose-800 dark:text-rose-200">{errorMessage}</p>
          </div>
        )}

        <Button
          type="submit"
          disabled={isSubmitting || isLoading || formData.password.length < 8 || formData.password !== formData.confirmPassword || !formData.gradeLevel}
          className="w-full h-12 text-base rounded-xl"
          data-testid="button-create-account"
        >
          {isSubmitting || isLoading ? 'Creating your account...' : 'Create account'}
        </Button>
        <p className="text-[11px] text-muted-foreground text-center">By continuing you agree to our terms and privacy policy.</p>
      </form>
    </AuthShell>
  );
}